import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { projectsAPI, newsletterAPI } from '../services/api';

function ProjectDetail() {
  const { id } = useParams();
  const [project, setProject] = useState(null);
  const [similarProjects, setSimilarProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [newsletterEmail, setNewsletterEmail] = useState('');
  const [newsletterStatus, setNewsletterStatus] = useState({ text: '', type: '' });
  const [newsletterLoading, setNewsletterLoading] = useState(false);

  useEffect(() => {
    fetchProject();
  }, [id]);

  const fetchProject = async () => {
    try {
      setLoading(true);
      const response = await projectsAPI.getById(id);
      if (response.data && response.data.success) {
        setProject(response.data.data);
        await fetchSimilarProjects(response.data.data.category);
      }
    } catch (error) {
      console.error('Error fetching project:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchSimilarProjects = async (category) => {
    try {
      const response = await projectsAPI.getAll();
      if (response.data && response.data.success) {
        const similar = response.data.data
          .filter(p => p.category === category && p.id !== parseInt(id))
          .slice(0, 3);
        setSimilarProjects(similar);
      }
    } catch (error) {
      console.error('Error fetching similar projects:', error);
    }
  };

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!newsletterEmail) return;
    try {
      setNewsletterLoading(true);
      setNewsletterStatus({ text: '', type: '' });
      const response = await newsletterAPI.subscribe(newsletterEmail);
      if (response.data.success) {
        setNewsletterStatus({ text: 'Thank you for subscribing!', type: 'success' });
        setNewsletterEmail('');
      } else {
        setNewsletterStatus({ text: response.data.message || 'Error subscribing', type: 'error' });
      }
    } catch (err) {
      console.error(err);
      setNewsletterStatus({ text: 'Connection error', type: 'error' });
    } finally {
      setNewsletterLoading(false);
      setTimeout(() => setNewsletterStatus({ text: '', type: '' }), 5000);
    }
  };

  const getCategoryLabel = (category) => {
    const labels = {
      mobility: 'International Mobility',
      solidarity: 'Solidarity & Development',
      human_rights: 'Human Rights',
      education: 'Education',
      environment: 'Environment'
    };
    return labels[category] || category;
  };

  const getStatusBadge = (status) => {
    const badges = {
      ongoing: '<span style="background:var(--primary-blue); color:white; padding:4px 12px; border-radius:20px; font-size:12px;">Ongoing</span>',
      upcoming: '<span style="background:#ffc107; color:#333; padding:4px 12px; border-radius:20px; font-size:12px;">Upcoming</span>',
      completed: '<span style="background:#6c757d; color:white; padding:4px 12px; border-radius:20px; font-size:12px;">Completed</span>'
    };
    return badges[status] || status;
  };

  const getAllTags = () => {
    const tags = [];
    if (project) {
      tags.push(project.status);
      tags.push(project.location?.split(',')[0]);
      tags.push('Solidarity');
      tags.push('Youth');
      tags.push('Europe');
    }
    return [...new Set(tags.filter(Boolean))];
  };

  const categories = [
    { value: 'all', label: 'All Projects' },
    { value: 'mobility', label: 'International Mobility' },
    { value: 'solidarity', label: 'Solidarity & Development' },
    { value: 'human_rights', label: 'Human Rights' },
    { value: 'education', label: 'Education' },
    { value: 'environment', label: 'Environment' }
  ];

  if (loading) {
    return (
      <main>
        <div className="slider-area2">
          <div className="slider-height2 d-flex align-items-center">
            <div className="container">
              <div className="row">
                <div className="col-xl-12">
                  <div className="hero-cap hero-cap2 pt-20 text-center">
                    <h2>Loading...</h2>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    );
  }

  if (!project) {
    return (
      <main>
        <div className="slider-area2">
          <div className="slider-height2 d-flex align-items-center">
            <div className="container">
              <div className="row">
                <div className="col-xl-12">
                  <div className="hero-cap hero-cap2 pt-20 text-center">
                    <h2>Project Not Found</h2>
                    <Link to="/projects" className="btn" style={{marginTop: '20px'}}>Back to Projects</Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main>
      {/* Hero Start */}
      <div className="slider-area2">
        <div className="slider-height2 d-flex align-items-center">
          <div className="container">
            <div className="row">
              <div className="col-xl-12">
                <div className="hero-cap hero-cap2 pt-70 text-center">
                  <h2>{project.title}</h2>
                  <div dangerouslySetInnerHTML={{ __html: getStatusBadge(project.status) }} />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Blog Area Start */}
      <section className="blog_area section-padding" className="about-low-area section-padding30" >
        <div className="container">
          <div className="row">
            {/* Main Content - Left Column */}
            <div className="col-lg-8 mb-5 mb-lg-0">
              <div className="blog_left_sidebar">
                <article className="blog_item">
                  <div className="blog_item_img">
                    <img 
                      className="card-img rounded-0" 
                      src={project.image_url || "/assets/img/gallery/case1.png"} 
                      alt={project.title} 
                    />
                    <div className="blog_item_date" style={{ backgroundColor: 'var(--primary-blue)' }}>
                      <h3>{new Date(project.start_date).getDate()}</h3>
                      <p>{new Date(project.start_date).toLocaleString('default', { month: 'short' })}</p>
                    </div>
                  </div>
                  <div className="blog_details">
                    <h2 className="blog-head" style={{color: '#1e293b', fontWeight: '700'}}>{project.title}</h2>
                    <p style={{color: 'var(--accent-red)', fontWeight: '700', marginBottom: '15px'}}>
                      📍 {project.location} | {getCategoryLabel(project.category)}
                    </p>
                    <p style={{lineHeight: '1.8', color: '#4b5563'}}>{project.description}</p>
                    
                    {/* Détails du projet */}
                    <div style={{marginTop: '30px', padding: '20px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0'}}>
                      <h4 style={{fontWeight: '700', color: 'var(--primary-blue)', marginBottom: '15px'}}>Project Details</h4>
                      <ul style={{listStyle: 'none', paddingLeft: '0'}}>
                        <li style={{marginBottom: '10px', fontSize: '14.5px'}}>📍 <strong>Location:</strong> {project.location}</li>
                        <li style={{marginBottom: '10px', fontSize: '14.5px'}}>📅 <strong>Start Date:</strong> {new Date(project.start_date).toLocaleDateString()}</li>
                        <li style={{marginBottom: '10px', fontSize: '14.5px'}}>📅 <strong>End Date:</strong> {new Date(project.end_date).toLocaleDateString()}</li>
                        <li style={{marginBottom: '10px', fontSize: '14.5px'}}>📊 <strong>Status:</strong> <span dangerouslySetInnerHTML={{ __html: getStatusBadge(project.status) }} /></li>
                      </ul>
                    </div>

                    {/* Boutons d'action */}
                    <div style={{marginTop: '30px', display: 'flex', gap: '15px', flexWrap: 'wrap'}}>
                      <a href="https://docs.google.com/forms/d/e/1FAIpQLSfDmock-placeholder-volunteer/viewform" target="_blank" rel="noopener noreferrer" className="btn" style={{ backgroundColor: 'var(--accent-red)', borderColor: 'var(--accent-red)' }}>Apply for this Project</a>
                      <Link to="/programs" className="btn white-btn">← Back to Programs</Link>
                    </div>
                  </div>
                </article>
              </div>
            </div>

            {/* Sidebar - Right Column */}
            <div className="col-lg-4">
              <div className="blog_right_sidebar">

                {/* Search Widget */}
                <aside className="single_sidebar_widget search_widget">
                  <form action="#">
                    <div className="form-group">
                      <div className="input-group mb-3">
                        <input 
                          type="text" 
                          className="form-control" 
                          placeholder="Search projects..." 
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              window.location.href = `/projects?search=${searchTerm}`;
                            }
                          }}
                        />
                        <div className="input-group-append">
                          <Link to={`/projects?search=${searchTerm}`} className="btns" style={{padding: '0 15px', display: 'flex', alignItems: 'center'}}>
                            <i className="ti-search"></i>
                          </Link>
                        </div>
                      </div>
                    </div>
                    <Link to={`/projects?search=${searchTerm}`} className="button rounded-0 primary-bg text-white w-100 btn_1 boxed-btn">
                      Search Projects
                    </Link>
                  </form>
                </aside>

                {/* Categories Widget */}
                <aside className="single_sidebar_widget post_category_widget">
                  <h4 className="widget_title" style={{color: '#2d2d2d'}}>Project Categories</h4>
                  <ul className="list cat-list">
                    {categories.map((cat) => (
                      <li key={cat.value}>
                        <Link to={`/projects?category=${cat.value}`} className="d-flex">
                          <p>{cat.label}</p>
                        </Link>
                      </li>
                    ))}
                  </ul>
                </aside>

                {/* Similar Projects Widget */}
                <aside className="single_sidebar_widget popular_post_widget">
                  <h3 className="widget_title" style={{color: '#2d2d2d'}}>Similar Projects</h3>
                  {similarProjects.length > 0 ? (
                    similarProjects.map((similar) => (
                      <div key={similar.id} className="media post_item">
                        <img src="/assets/img/gallery/case1.png" alt={similar.title} style={{width: '80px', borderRadius: '8px'}} />
                        <div className="media-body">
                          <Link to={`/projects/${similar.id}`}>
                            <h3 style={{color: '#2d2d2d', fontSize: '16px'}}>{similar.title}</h3>
                          </Link>
                          <p>{getCategoryLabel(similar.category)}</p>
                        </div>
                      </div>
                    ))
                  ) : (
                    <p>No similar projects found.</p>
                  )}
                </aside>

                {/* Tag Clouds Widget */}
                <aside className="single_sidebar_widget tag_cloud_widget">
                  <h4 className="widget_title" style={{color: '#2d2d2d'}}>Tag Clouds</h4>
                  <ul className="list">
                    {getAllTags().map((tag, index) => (
                      <li key={index}>
                        <Link to={`/projects?search=${tag}`}>#{tag}</Link>
                      </li>
                    ))}
                    <li><Link to="/projects?category=mobility">#Mobility</Link></li>
                    <li><Link to="/projects?category=solidarity">#Solidarity</Link></li>
                    <li><Link to="/projects?category=human_rights">#HumanRights</Link></li>
                    <li><Link to="/projects?category=education">#Education</Link></li>
                    <li><Link to="/projects?category=environment">#Environment</Link></li>
                    <li><Link to="/contact">#Volunteer</Link></li>
                    <li><Link to="/contact">#JoinUs</Link></li>
                  </ul>
                </aside>

                {/* Instagram Feeds Widget - Comme dans l'original */}
                <aside className="single_sidebar_widget instagram_feeds">
                  <h4 className="widget_title" style={{color: '#2d2d2d'}}>Instagram Feeds</h4>
                  <ul className="instagram_row flex-wrap">
                    <li>
                      <a href="#">
                        <img className="img-fluid" src="/assets/img/post/post_5.png" alt="Instagram" />
                      </a>
                    </li>
                    <li>
                      <a href="#">
                        <img className="img-fluid" src="/assets/img/post/post_6.png" alt="Instagram" />
                      </a>
                    </li>
                    <li>
                      <a href="#">
                        <img className="img-fluid" src="/assets/img/post/post_7.png" alt="Instagram" />
                      </a>
                    </li>
                    <li>
                      <a href="#">
                        <img className="img-fluid" src="/assets/img/post/post_8.png" alt="Instagram" />
                      </a>
                    </li>
                    <li>
                      <a href="#">
                        <img className="img-fluid" src="/assets/img/post/post_9.png" alt="Instagram" />
                      </a>
                    </li>
                    <li>
                      <a href="#">
                        <img className="img-fluid" src="/assets/img/post/post_10.png" alt="Instagram" />
                      </a>
                    </li>
                  </ul>
                </aside>

                {/* Newsletter Widget */}
                <aside className="single_sidebar_widget newsletter_widget">
                  <h4 className="widget_title" style={{color: '#2d2d2d'}}>Newsletter</h4>
                  <form onSubmit={handleNewsletterSubmit}>
                    <div className="form-group">
                      <input 
                        type="email" 
                        className="form-control" 
                        placeholder="Enter email" 
                        value={newsletterEmail}
                        onChange={(e) => setNewsletterEmail(e.target.value)}
                        required 
                      />
                    </div>
                    <button className="button rounded-0 primary-bg text-white w-100 btn_1 boxed-btn" type="submit" disabled={newsletterLoading}>
                      {newsletterLoading ? 'Subscribing...' : 'Subscribe'}
                    </button>
                  </form>
                  {newsletterStatus.text && (
                    <div className="alert mt-3" style={{
                      fontSize: '13px',
                      padding: '10px',
                      background: newsletterStatus.type === 'success' ? '#d4edda' : '#f8d7da',
                      color: newsletterStatus.type === 'success' ? '#155724' : '#721c24',
                      border: `1px solid ${newsletterStatus.type === 'success' ? '#c3e6cb' : '#f5c6cb'}`,
                      borderRadius: '6px'
                    }}>
                      {newsletterStatus.text}
                    </div>
                  )}
                </aside>

              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

export default ProjectDetail;